
import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, Volume2, Loader2, Zap, Share2, X, NotebookTabs, 
  History, Sparkles, BookOpen, Star, Copy, Check, Palette, 
  Bookmark, ChevronRight, Info, Trash2
} from 'lucide-react';
import { AppLanguage, SavedWord } from '../types';
import { dictionaryLookup, generateSpeech, generateImage } from '../services/gemini';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const SUGGESTED_WORDS = ['Inntung', 'Lainatna', 'Heritage', 'Pau', 'Zomi'];

interface DictionaryViewProps {
  onUseCredit: () => boolean;
  language: AppLanguage;
  onSearchPerformed?: (word: string) => void;
}

const DictionaryView: React.FC<DictionaryViewProps> = ({ onUseCredit, language, onSearchPerformed }) => {
  const [word, setWord] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGeneratingImg, setIsGeneratingImg] = useState(false);
  const [generatedImg, setGeneratedImg] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [savedWords, setSavedWords] = useState<SavedWord[]>([]);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'search' | 'saved'>('search');
  
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    const savedHistory = localStorage.getItem('zomigenai_search_history');
    if (savedHistory) setHistory(JSON.parse(savedHistory));
    
    const savedItems = localStorage.getItem('zomigenai_saved_lexicon');
    if (savedItems) setSavedWords(JSON.parse(savedItems));
  }, []);

  const handleLookup = async (targetWord?: string) => {
    const searchWord = targetWord || word.trim();
    if (!searchWord || isLoading) return;
    if (!onUseCredit()) return;
    
    if (targetWord) setWord(targetWord);
    setIsLoading(true);
    setResult(null);
    setGeneratedImg(null);
    setViewMode('search');

    // Update History
    const updatedHistory = [searchWord, ...history.filter(h => h !== searchWord)].slice(0, 8);
    setHistory(updatedHistory);
    localStorage.setItem('zomigenai_search_history', JSON.stringify(updatedHistory));

    try {
      const data = await dictionaryLookup(searchWord);
      setResult(data || null);
      if (onSearchPerformed) onSearchPerformed(searchWord);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSave = () => {
    if (!word || !result) return;
    const isAlreadySaved = savedWords.find(sw => sw.word.toLowerCase() === word.toLowerCase());
    let newSaved: SavedWord[];
    
    if (isAlreadySaved) {
      newSaved = savedWords.filter(sw => sw.word.toLowerCase() !== word.toLowerCase());
    } else {
      newSaved = [{ word, definition: result, timestamp: Date.now() }, ...savedWords];
    }
    
    setSavedWords(newSaved);
    localStorage.setItem('zomigenai_saved_lexicon', JSON.stringify(newSaved));
  };

  const handleGenerateIllustration = async () => {
    if (!word || isGeneratingImg) return;
    if (!onUseCredit()) return;
    setIsGeneratingImg(true);
    try {
      const imgUrl = await generateImage(`A visual representation of the concept "${word}" in Zomi culture, high-quality, symbolic illustration`, false);
      setGeneratedImg(imgUrl);
    } catch (err) { console.error(err); } finally { setIsGeneratingImg(false); }
  };

  const handleListen = async () => {
    if (!word || isSpeaking) return;
    setIsSpeaking(true);
    try {
      const base64Audio = await generateSpeech(word);
      if (base64Audio) {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        }
        const ctx = audioContextRef.current;
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const dataInt16 = new Int16Array(bytes.buffer);
        const buffer = ctx.createBuffer(1, dataInt16.length, 24000);
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < dataInt16.length; i++) channelData[i] = dataInt16[i] / 32768.0;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => setIsSpeaking(false);
        source.start(0);
      }
    } catch (error) { setIsSpeaking(false); }
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isSaved = savedWords.some(sw => sw.word.toLowerCase() === word.toLowerCase());

  const Renderers = {
    h1: ({ children }: any) => <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-5 tracking-tight border-b-2 border-blue-600 w-fit pb-1">{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-[10px] font-bold text-blue-600 dark:text-blue-400 mt-6 mb-2 uppercase tracking-widest flex items-center gap-2">
      <span className="w-6 h-[1px] bg-blue-600/30" /> {children}
    </h2>,
    p: ({ children }: any) => <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-3">{children}</p>,
    table: ({ children }: any) => (
      <div className="my-4 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse bg-slate-50/50 dark:bg-white/[0.01]">
            {children}
          </table>
        </div>
      </div>
    ),
    thead: ({ children }: any) => <thead className="bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white font-bold">{children}</thead>,
    th: ({ children }: any) => <th className="p-3 text-[9px] uppercase tracking-wider">{children}</th>,
    td: ({ children }: any) => <td className="p-3 border-t border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400">{children}</td>,
    strong: ({ children }: any) => <strong className="text-slate-900 dark:text-white font-bold">{children}</strong>
  };

  return (
    <div className="w-full h-full flex flex-col bg-[#F8FAFC] dark:bg-[#050505] overflow-y-auto custom-scrollbar">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-5 py-6 sm:py-12 flex flex-col items-center">
        
        {/* Branding Section */}
        <header className="text-center mb-8 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white dark:bg-white/5 rounded-full border border-slate-200 dark:border-white/10 mb-4 shadow-sm">
             <NotebookTabs size={12} className="text-blue-600" />
             <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 tracking-wider">Lexicon Architecture</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-2 tracking-tighter">Dictionary</h1>
          <p className="text-slate-500 dark:text-slate-500 font-medium text-xs md:text-sm max-w-xs mx-auto">
            High-fidelity Zomi-English linguistic intelligence for scholars and enthusiasts.
          </p>
        </header>

        {/* Tabs */}
        <nav className="flex bg-slate-200/50 dark:bg-white/5 p-1 rounded-xl mb-8 border border-slate-200 dark:border-white/5 flex-shrink-0">
           {['search', 'saved'].map((mode) => (
             <button 
               key={mode}
               onClick={() => setViewMode(mode as any)}
               className={`px-4 sm:px-6 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all duration-300 ${viewMode === mode ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
             >
               {mode === 'search' ? 'Search' : `Saved (${savedWords.length})`}
             </button>
           ))}
        </nav>

        {viewMode === 'search' ? (
          <div className="w-full max-w-2xl space-y-8 flex-1 flex flex-col">
            {/* Search Bar - Responsive Fix */}
            <div className="relative w-full group flex-shrink-0">
              <div className={`flex items-center bg-white dark:bg-[#0A0A0A] border-2 rounded-2xl p-1 transition-all duration-500 ${isLoading ? 'border-blue-500/50' : 'border-slate-100 dark:border-white/5 focus-within:border-blue-500/30 shadow-sm'}`}>
                <div className="pl-3 text-slate-300 group-focus-within:text-blue-500 hidden xs:block">
                  <Search size={18} />
                </div>
                <input
                  type="text"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                  placeholder="Type a word..."
                  className="flex-1 bg-transparent border-none text-slate-900 dark:text-white px-2 sm:px-3 py-2.5 focus:outline-none text-sm md:text-base font-semibold placeholder:text-slate-300 dark:placeholder:text-slate-800 min-w-0"
                />
                <div className="flex items-center gap-1.5 pr-1 flex-shrink-0">
                  {word && <button onClick={() => setWord('')} className="p-1.5 text-slate-300 hover:text-red-500"><X size={16} /></button>}
                  <button
                    onClick={() => handleLookup()}
                    disabled={!word.trim() || isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-6 py-2 rounded-xl font-bold text-[11px] transition-all disabled:opacity-50 flex items-center gap-2 shadow-md active:scale-95"
                  >
                    {isLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} fill="currentColor" />}
                    <span className="hidden xs:inline">Lookup</span>
                    <span className="xs:hidden">Go</span>
                  </button>
                </div>
              </div>
            </div>

            {!result && !isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-up">
                <div className="bg-white dark:bg-white/[0.01] border border-slate-100 dark:border-white/5 rounded-2xl p-6">
                  <h3 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Sparkles size={12} className="text-blue-500" /> Trending Now
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {SUGGESTED_WORDS.map((w) => (
                      <button key={w} onClick={() => handleLookup(w)} className="px-3 py-1.5 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5 rounded-lg text-[10px] font-semibold text-slate-600 dark:text-slate-400 hover:bg-blue-600 hover:text-white transition-all">
                        {w}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-white/[0.01] border border-slate-100 dark:border-white/5 rounded-2xl p-6">
                  <h3 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <History size={12} className="text-blue-500" /> Recent History
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {history.length > 0 ? history.map((h, i) => (
                      <button key={i} onClick={() => handleLookup(h)} className="px-3 py-1.5 bg-slate-50 dark:bg-white/[0.01] rounded-lg text-[10px] font-medium text-slate-500 hover:text-blue-600 transition-colors">
                        {h}
                      </button>
                    )) : <p className="text-[10px] text-slate-400 italic">No history yet.</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Results */}
            {result && (
              <div className="w-full animate-fade-up">
                <div className="bg-white dark:bg-[#0A0A0A] border border-slate-100 dark:border-white/5 rounded-3xl overflow-hidden shadow-xl flex flex-col lg:flex-row min-h-0">
                  <div className="flex-1 p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-slate-50 dark:border-white/5">
                    <div className="flex justify-between items-start mb-6">
                       <span className="px-2.5 py-1 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg text-[9px] font-bold">Verified Result</span>
                       <div className="flex gap-1.5">
                          <button onClick={handleCopy} className="p-2 bg-slate-50 dark:bg-white/5 rounded-lg text-slate-400 hover:text-blue-600 transition-all">
                            {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                          </button>
                          <button onClick={handleToggleSave} className={`p-2 bg-slate-50 dark:bg-white/5 rounded-lg transition-all ${isSaved ? 'text-blue-600' : 'text-slate-400 hover:text-blue-600'}`}>
                            <Star size={16} fill={isSaved ? "currentColor" : "none"} />
                          </button>
                       </div>
                    </div>

                    <article className="prose prose-slate dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={Renderers}>{result}</ReactMarkdown>
                    </article>
                  </div>

                  <div className="w-full lg:w-64 bg-slate-50/30 dark:bg-white/[0.01] p-6 space-y-6 flex-shrink-0">
                    <div>
                       <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Palette size={12} /> Illustration</h4>
                       <div className="aspect-square rounded-xl overflow-hidden border border-dashed border-slate-200 dark:border-white/10 relative group">
                          {generatedImg ? (
                            <>
                              <img src={generatedImg} className="w-full h-full object-cover" alt="Visual aid" />
                              <button onClick={() => setGeneratedImg(null)} className="absolute top-1.5 right-1.5 p-1 bg-black/50 text-white rounded-full"><Trash2 size={10}/></button>
                            </>
                          ) : (
                            <button onClick={handleGenerateIllustration} disabled={isGeneratingImg} className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400 hover:bg-blue-600/5 transition-colors">
                              {isGeneratingImg ? <Loader2 size={18} className="animate-spin text-blue-600" /> : <><Palette size={20} /><span className="text-[9px] font-bold">Visual AI</span></>}
                            </button>
                          )}
                       </div>
                    </div>

                    <div>
                       <h4 className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Volume2 size={12} /> Audio</h4>
                       <button 
                        onClick={handleListen} 
                        disabled={isSpeaking}
                        className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${isSpeaking ? 'bg-blue-600 text-white' : 'bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white shadow-sm'}`}
                       >
                         <Volume2 size={16} className={isSpeaking ? 'animate-bounce' : ''} />
                         <span className="text-[10px] font-bold uppercase tracking-wider">Play Audio</span>
                       </button>
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-white/10">
                       <div className="flex items-center gap-2 text-slate-300 text-[10px] font-bold uppercase">
                         <BookOpen size={12} />
                         Neural Engine V3
                       </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full max-w-xl animate-fade-up flex-1">
            {savedWords.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-white/[0.01] rounded-2xl border border-dashed border-slate-200 dark:border-white/10">
                 <Bookmark size={40} className="mx-auto text-slate-200 dark:text-slate-800 mb-4" />
                 <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-1">Lexicon Empty</h3>
                 <p className="text-xs text-slate-400 mb-6">Words you save for later will appear here.</p>
                 <button onClick={() => setViewMode('search')} className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest">Explore Dictionary</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {savedWords.map((sw, idx) => (
                  <div key={idx} onClick={() => { setWord(sw.word); handleLookup(sw.word); }} className="group p-5 bg-white dark:bg-[#0A0A0A] border border-slate-100 dark:border-white/5 rounded-2xl hover:border-blue-500/50 transition-all cursor-pointer shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                       <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 font-bold text-base flex-shrink-0">
                          {sw.word.charAt(0).toUpperCase()}
                       </div>
                       <div className="min-w-0">
                         <h3 className="text-base font-bold text-slate-900 dark:text-white tracking-tight truncate">{sw.word}</h3>
                         <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                           <History size={10} /> {new Date(sw.timestamp).toLocaleDateString()}
                         </p>
                       </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                       <button onClick={(e) => { e.stopPropagation(); const n = savedWords.filter(i => i.word !== sw.word); setSavedWords(n); localStorage.setItem('zomigenai_saved_lexicon', JSON.stringify(n)); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                         <Trash2 size={16} />
                       </button>
                       <ChevronRight size={18} className="text-slate-200 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DictionaryView;
